import { memo, type RowSelectionState } from '@tanstack/table-core';
import { useCallback } from 'react';

import { RowSelectionCell, RowSelectionHeader } from './RowSelectionCells.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableV2ConfigElement } from '../../../hooks/useDataTableV2Config.js';
import type {
  ColumnDef,
  Row,
  RowData,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import type { DataTableV2RowData } from '../../public.api.js';

/**
 * @public
 */
export interface DataTableV2RowSelectionBaseProps<
  TData extends DataTableV2RowData,
> {
  /**
   * Enables row selection. Either accepts a boolean to enable the
   * row selection on the entire table or a config object.
   * The config object accepts a function that will get the row data as
   * the parameter and should return true or false if the row should be
   * selectable or not. In the config object, the 'select all' behavior
   * can also be configured and a selection 'limit' can also be set.
   *
   * Note: For the case where this prop is a config object, it needs
   * to be memoized, to avoid updates if it is identical. Same as for all
   * for other props with type object.
   *
   * @defaultValue false
   */
  selectableRows?:
    | boolean
    | {
        /**
         * Allows to disable the selection of certain rows.
         */
        disableRowSelection?: (row: TData) => boolean;

        /**
         * Allows to configure if the select all will only select
         * the current page or the entire.
         * @defaultValue 'page'
         */
        selectAllBehavior?: 'page' | 'all';

        /**
         * Allows to set a number limit on how many items are allowed
         * to be selected. Limit number needs to be a positive integer.
         */
        limit?: number;
      };
  /**
   * Callback triggered when selection is changed.
   */
  onRowSelectionChange?: (selectedRows: Record<string, boolean>) => void;
}

/**
 * @public
 */
export interface DataTableV2RowSelectionControlledProps<
  TData extends DataTableV2RowData,
> extends DataTableV2RowSelectionBaseProps<TData> {
  /**
   * Controlled property to define the selected rows.
   **/
  selectedRows?: Record<string, boolean>;
  /**
   * Uncontrolled property to define the selected rows.
   **/
  defaultSelectedRows?: never;
}

/**
 * @public
 */
export interface DataTableV2RowSelectionUncontrolledProps<
  TData extends DataTableV2RowData,
> extends DataTableV2RowSelectionBaseProps<TData> {
  /**
   * Controlled property to define the selected rows.
   **/
  defaultSelectedRows?: Record<string, boolean>;
  /**
   * Uncontrolled property to define the selected rows.
   **/
  selectedRows?: never;
}

/**
 * Options for DataTableV2 row selection
 * @public
 */
export type DataTableV2RowSelectionProps<TData extends DataTableV2RowData> =
  | DataTableV2RowSelectionControlledProps<TData>
  | DataTableV2RowSelectionUncontrolledProps<TData>;

/**
 * Extension interface for the table options
 * @internal
 */
export interface DataTableV2RowSelectionOptions<
  TData extends DataTableV2RowData,
> {
  /**
   * Defines the select all mode for the row selection in the header.
   * @defaultValue 'page'
   */
  rowSelectionSelectAllMode?: 'page' | 'all';
  /**
   * Defines a limit of the maximum amount of elements that should be selectable.
   */
  rowSelectionLimit?: number;
  /**
   * - Enables/disables row selection for all rows in the table OR
   * - A function that given a row, returns whether to enable/disable row selection for that row
   */
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
}

/**
 * Extension interface for the table instance
 * @internal
 */
export interface DataTableV2RowSelectionInstance {
  /**
   * Returns a count of currently selected elements.
   */
  getSelectionCount: () => number;
  /**
   * Returns a handler that can be used to toggle all rows on the current page respecting the configured limit.
   */
  getToggleAllLimitedPageRowsSelectedHandler: () => (event: unknown) => void;
  /**
   * Returns a handler that can be used to toggle all rows in the table respecting the configured limit.
   */
  getToggleAllLimitedRowsSelectedHandler: () => (event: unknown) => void;
  /**
   * Selects/deselects all rows on the current page respecting the configured limit.
   */
  toggleAllLimitedPageRowsSelected: (value?: boolean) => void;
  /**
   * Selects/deselects all rows in the table respecting the configured limit.
   */
  toggleAllLimitedRowsSelected: (value?: boolean) => void;
  /**
   *  Returns whether row selection limit is reached.
   */
  getIsRowSelectionLimitReached: (
    rowSelectionState?: Record<string, boolean>,
  ) => boolean;
  /**
   * Returns true, if row selection is disabled for all rows on the current page, false otherwise.
   */
  getIsAllPageRowsSelectionDisabled: () => boolean;
  /**
   * Returns true, if row selection is disabled for all rows in the table, false otherwise.
   */
  getIsAllRowsSelectionDisabled: () => boolean;
}

/**
 * Extension interface to extend the types of the `Row` from the tanstack table.
 * @internal
 */
export interface DataTableV2RowSelectionRow {
  /**
   * Selects/deselects one or multiple rows. When the shift key is pressed while selecting, all rows between this and the previous selection are selected/deselected.
   */
  batchSelect: (value?: boolean, shiftKey?: boolean) => void;
  /**
   * Selects/deselects the row.
   */
  toggleSelected: (value?: boolean) => void;
  /**
   *  Returns whether row selection should be disabled.
   */
  getShouldDisableRowSelection: () => boolean;
}

/**
 * Helper function to identify the ROW_SELECTION_COLUMN
 * @internal
 */
export function isRowSelectionColumnDef<TData extends DataTableV2RowData>(
  column: ColumnDef<TData>,
): boolean {
  return column.id === ROW_SELECTION_COLUMN.id;
}

/**
 * Column definition for the selection column
 * @internal
 */
export const ROW_SELECTION_COLUMN: ColumnDef<unknown, unknown> = {
  id: 'DataTableV2RowSelectionColumn',
  alignment: 'left',
  accessorFn: () => '',
  header: ({ table }) => (
    <RowSelectionHeader
      table={table}
      resetBatchSelection={resetBatchSelection}
    />
  ),
  width: 'content',
  minWidth: 24,
  enableSorting: false,
  enableResizing: false,
  enableUserActions: false,
  enableHiding: false,
  meta: {
    builtin: true,
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
    builtinPlacement: 'prefix',
    cellDeps: (cell) => {
      return [cell.row.getIsSelected(), cell.row.getCanSelect()];
    },
  },
  cell: ({ row, table }) => <RowSelectionCell row={row} table={table} />,
};

let batchSelectionRowId: string | undefined = undefined;
let previousSelectionRange: { start: number; end: number } | undefined =
  undefined;

const resetBatchSelection = (event: MouseEvent) => {
  // only reset the selection if the click was outside the checkbox and previous selection is saved
  if (
    (batchSelectionRowId || previousSelectionRange) &&
    !(event.target as HTMLElement).closest('[data-row-selection-checkbox]')
  ) {
    batchSelectionRowId = undefined;
    previousSelectionRange = undefined;
  }
};

const mutateRowIsSelected = <TData extends RowData>(
  selectedRowIds: Record<string, boolean>,
  id: string,
  value: boolean,
  includeChildren: boolean,
  table: Table<TData>,
) => {
  const row = table.getRow(id, true);

  if (value) {
    if (
      row.getCanSelect() &&
      !table.getIsRowSelectionLimitReached(selectedRowIds)
    ) {
      selectedRowIds[id] = true;
    }
  } else {
    delete selectedRowIds[id];
  }

  if (
    includeChildren &&
    row.subRows?.length &&
    row.getCanSelectSubRows() &&
    !table.getIsRowSelectionLimitReached(selectedRowIds)
  ) {
    row.subRows.forEach((row) =>
      mutateRowIsSelected(
        selectedRowIds,
        row.id,
        value,
        includeChildren,
        table,
      ),
    );
  }
};

/**
 * @internal
 */
export const DataTableV2RowSelection: TableFeature = {
  createTable<TData extends DataTableV2RowData>(table: Table<TData>): void {
    table.getToggleAllLimitedRowsSelectedHandler = () => {
      return (e: unknown) => {
        table.toggleAllLimitedRowsSelected(
          ((e as MouseEvent).target as HTMLInputElement).checked,
        );
      };
    };

    table.getToggleAllLimitedPageRowsSelectedHandler = () => {
      return (e: unknown) => {
        table.toggleAllLimitedPageRowsSelected(
          ((e as MouseEvent).target as HTMLInputElement).checked,
        );
      };
    };

    table.getToggleAllPageRowsSelectedHandler = () => {
      return (e: unknown) => {
        table.toggleAllPageRowsSelected(
          ((e as MouseEvent).target as HTMLInputElement).checked,
        );
      };
    };

    table.getSelectionCount = memo(
      () => [table.getFilteredSelectedRowModel()],
      (filteredSelectedRows) => {
        return filteredSelectedRows.flatRows.length;
      },
      getMemoOptions(table.options, 'debugTable', 'table.getSelectionCount'),
    );

    // You can access `rows`, `flatRows` and `rowsById` on the row model.
    // Somehow the expanded detail rows are contained in the row model's `rowsById` which should actually not happen
    // since we only adapt `rows` in our `getRowDetailsRowModel`.
    // This leads to an issue when toggling the selection of all rows on the page:
    // Tanstack's implementation of `toggleAllPageRowsSelected` iterates over `rows` and would therefore try to select
    // the expanded detail row which is not possible and causes the table to crash.
    // Therefore, we need to overwrite this method and iterate over the `flatRows` to make sure only selectable rows
    // are (de)selected.
    table.toggleAllPageRowsSelected = (value) => {
      table.setRowSelection((old) => {
        const resolvedValue =
          typeof value !== 'undefined'
            ? value
            : !table.getIsAllPageRowsSelected();

        const rowSelection: RowSelectionState = { ...old };

        table.getRowModel().flatRows.forEach((row) => {
          mutateRowIsSelected(rowSelection, row.id, resolvedValue, true, table);
        });

        return rowSelection;
      });
    };

    table.toggleAllLimitedPageRowsSelected = (value?: boolean) => {
      // Figure out what the target should be, as the "select all" can also be
      // indeterminate. Next steps are
      // false -> true
      // 'indeterminate' -> true
      // true -> false
      const resolvedValue =
        typeof value !== 'undefined'
          ? value
          : !table.getIsAllPageRowsSelected();

      // If we are triggering towards deselect, we can still use the limitless
      // selection
      if (resolvedValue === false) {
        table.toggleAllPageRowsSelected(false);
        return;
      }

      table.setRowSelection((old) => {
        const rowSelection: RowSelectionState = { ...old };
        const limit = table.options.rowSelectionLimit ?? 0;

        // Figure out how many items are already selected.
        const currentSelectionLength = table.getSelectionCount();

        // If there are fewer elements selected than the limit we can fill the
        // selection.
        if (currentSelectionLength < limit) {
          const rows = table.getRowModel().rows;
          let currentSelectionCount = currentSelectionLength;
          for (const row of rows) {
            // If the row cannot be selected, or the element is already selected, we can skip over it
            if (!row.getCanSelect() || row.getIsSelected()) {
              continue;
            }
            rowSelection[row.id] = true;
            currentSelectionCount += 1;

            if (currentSelectionCount >= limit) {
              break;
            }
          }
        }
        return rowSelection;
      });
    };

    table.toggleAllLimitedRowsSelected = (value?: boolean) => {
      // Figure out what the target should be, as the "select all" can also be
      // indeterminate. Next steps are
      // false -> true
      // 'indeterminate' -> true
      // true -> false
      const resolvedValue =
        typeof value !== 'undefined'
          ? value
          : !table.getIsAllPageRowsSelected();

      // If we are triggering towards deselect, we can still use the limitless
      // selection
      if (resolvedValue === false) {
        table.toggleAllRowsSelected(false);
        return;
      }

      table.setRowSelection((old) => {
        const rowSelection: RowSelectionState = { ...old };
        const limit = table.options.rowSelectionLimit ?? 0;

        // Figure out how many items are already selected.
        const currentSelectionLength = table.getSelectionCount();

        // If there are fewer elements selected than the limit we can fill the
        // selection.
        if (currentSelectionLength < limit) {
          const rows = table.getPreGroupedRowModel().flatRows;
          let currentSelectionCount = currentSelectionLength;
          for (const row of rows) {
            // If the row cannot be selected, or the element is already selected, we can skip over it
            if (!row.getCanSelect() || row.getIsSelected()) {
              continue;
            }
            rowSelection[row.id] = true;
            currentSelectionCount += 1;

            if (currentSelectionCount >= limit) {
              break;
            }
          }
        }
        return rowSelection;
      });
    };

    table.getIsRowSelectionLimitReached = (rowSelectionState) => {
      const rowSelection = rowSelectionState || table.getState().rowSelection;
      const rowSelectionLimit = table.options.rowSelectionLimit;
      const selectionCount = Object.keys(rowSelection).length;

      return (
        rowSelectionLimit !== undefined &&
        rowSelectionLimit !== 0 &&
        selectionCount >= rowSelectionLimit
      );
    };

    table.getIsAllPageRowsSelected = () => {
      // if there is a limit for how many rows can be selected, and it is reached, immediately return true
      if (table.getIsRowSelectionLimitReached()) {
        return true;
      }

      // check if there are any enabled, unselected rows on the current page
      return !table
        .getPaginationRowModel()
        .flatRows.some((row) => row.getCanSelect() && !row.getIsSelected());
    };

    table.getIsAllRowsSelected = () => {
      // if there is a limit for how many rows can be selected, and it is reached, immediately return true
      if (table.getIsRowSelectionLimitReached()) {
        return true;
      }

      // check if there are any enabled, unselected rows
      return !table
        .getFilteredRowModel()
        .flatRows.some((row) => row.getCanSelect() && !row.getIsSelected());
    };

    table.getIsSomeRowsSelected = () => {
      const paginationFlatRows = table.getFilteredRowModel().flatRows;
      return table.getIsAllRowsSelected()
        ? false
        : paginationFlatRows
            .filter((row) => row.getCanSelect())
            .some((d) => d.getIsSelected() || d.getIsSomeSelected());
    };

    table.getIsAllPageRowsSelectionDisabled = () => {
      return table
        .getPaginationRowModel()
        .flatRows.every((row) => row.getShouldDisableRowSelection());
    };

    table.getIsAllRowsSelectionDisabled = () => {
      return table
        .getPrePaginationRowModel()
        .flatRows.every((row) => row.getShouldDisableRowSelection());
    };
  },

  createRow<TData extends DataTableV2RowData>(
    row: Row<TData>,
    table: Table<TData>,
  ) {
    row.getShouldDisableRowSelection = () => {
      const { enableRowSelection } = table.options;
      const isRowSelectionEnabled =
        typeof enableRowSelection === 'function'
          ? enableRowSelection(row)
          : enableRowSelection ?? true;

      // Selected rows should always be deselectable when limits are defined.
      // Otherwise you could end up in a situation that you have reached a limit
      // but all options that would be deselectable to go under the limit are disabled.
      if (row.getIsSelected()) {
        return false;
      }

      return !isRowSelectionEnabled || table.getIsRowSelectionLimitReached();
    };

    row.getCanSelect = () => {
      const isParentRow = !!row.subRows?.length;

      if (isParentRow) {
        return false;
      }

      return typeof table.options.enableRowSelection === 'function'
        ? table.options.enableRowSelection(row)
        : table.options.enableRowSelection ?? true;
    };

    row.toggleSelected = (value) => {
      const isSelected = row.getIsSelected();

      table.setRowSelection((old) => {
        value = typeof value !== 'undefined' ? value : !isSelected;
        const selectedRowIds = { ...old };

        if (row.getCanSelect() && isSelected === value) {
          return old;
        }

        mutateRowIsSelected(selectedRowIds, row.id, value, true, table);

        return selectedRowIds;
      });
    };

    /**
     * Handles row selection/deselection with support for batch selection using Shift+click. If Shift is not pressed,
     * or no previous selection exists, the row is simply selected/deselected and becomes the reference for a possible batch selection.
     * If a batch selection reference exists, and the user selects a row while holding Shift, all rows in between are selected/deselected.
     * If the user then selects another row with the Shift pressed, the previous batch selection is removed, and only the new one is selected.
     * The batch selection reference is reset, once the user clicks anywhere in the table outside the checkboxes.
     */
    row.batchSelect = (value?: boolean, shiftKey?: boolean) => {
      // if last selected row is undefined, or if the shift key is not pressed, simply toggle the single row selection
      if (batchSelectionRowId === undefined || !shiftKey) {
        row.toggleSelected(value);
        batchSelectionRowId = row.id;
        previousSelectionRange = undefined;
        return;
      }

      // if the shift key is pressed and there is a previous selection
      // get the start and end index of the batch selection
      const flatRows = table.getSortedRowModel().flatRows;
      const firstSelectedIndex = flatRows.findIndex(
        (flatRow) => batchSelectionRowId && flatRow.id === batchSelectionRowId,
      );
      const currentSelectedIndex = flatRows.findIndex(
        (flatRow) => flatRow.id === row.id,
      );

      const startIndex = Math.min(firstSelectedIndex, currentSelectedIndex);
      const endIndex = Math.max(firstSelectedIndex, currentSelectedIndex);

      const shouldUpdateChildren = (idx: number) => endIndex === idx;

      table.setRowSelection((old) => {
        const rowSelection: RowSelectionState = { ...old };
        // the batch selection checked value will be the same as for the initial selection
        const firstSelectedCheckedValue =
          flatRows[firstSelectedIndex].getIsSelected();

        if (previousSelectionRange) {
          const start = previousSelectionRange.start;
          const end = previousSelectionRange.end;
          // deselect the previous range
          for (let i = start; i <= end; i++) {
            mutateRowIsSelected(
              rowSelection,
              flatRows[i].id,
              false,
              true,
              table,
            );
          }
        }

        // select/deselect current range
        for (let i = startIndex; i <= endIndex; i++) {
          mutateRowIsSelected(
            rowSelection,
            flatRows[i].id,
            firstSelectedCheckedValue,
            shouldUpdateChildren(i),
            table,
          );
        }

        // if there was a selection, save the range
        previousSelectionRange = firstSelectedCheckedValue
          ? { start: startIndex, end: endIndex }
          : undefined;

        return rowSelection;
      });
    };
  },
};

/**
 * Configuration hook for the DataTableV2 UserActions feature
 * @internal
 */
export function useRowSelection<TData extends DataTableV2RowData>(
  props: DataTableV2RowSelectionProps<TData>,
  options: TableOptions<TData>,
) {
  const configSelectableRows = useDataTableV2ConfigElement('selectableRows');
  // Need to move that into a const here in order to make the typechecks
  // work, because otherwise the selectableRows member could switch.

  // note: for case where the selectableRows prop is an object, it needs
  // to be memoized by the consumer, to avoid updates if it is identical
  const selectableRows = props.selectableRows ?? configSelectableRows;
  // options.enableRowSelection is used as dependency in useBuiltinColumns, therefore we need to cache this function to avoid infinite rerenders
  const enableRowSelectionCallback = useCallback(
    (row: Row<TData>) => {
      // If there is a function defined that will allow for disabling certain
      // row selections, we can apply this one.
      if (
        selectableRows !== undefined &&
        typeof selectableRows !== 'boolean' &&
        selectableRows.disableRowSelection
      ) {
        return !selectableRows.disableRowSelection(row.original);
      }
      return true;
    },
    [selectableRows],
  );

  if (selectableRows !== undefined) {
    options.enableRowSelection = true;
    let rowSelectionMode: DataTableV2RowSelectionOptions<TData>['rowSelectionSelectAllMode'] =
      'page';

    // If selectableRows is controlled via a boolean, we can default the options
    if (selectableRows === true || selectableRows === false) {
      options.enableRowSelection = selectableRows;
    } else {
      const { selectAllBehavior, limit } = selectableRows;

      options.enableRowSelection = enableRowSelectionCallback;

      // Change the rowSelectionMode for the _select all_ checkbox if it is defined.
      if (selectAllBehavior) {
        rowSelectionMode = selectAllBehavior;
      }

      // Defines a row selection limit for the maximum count of rows that are
      // selectable.
      options.rowSelectionLimit =
        limit !== undefined && limit >= 0 ? limit : undefined;
    }

    options.enableMultiRowSelection = true;
    options.rowSelectionSelectAllMode = rowSelectionMode;
  } else {
    options.enableRowSelection = false;
  }

  const [selectedRows, setSelectedRows] = useControllableState<
    Record<string, boolean>
  >({
    value: props.selectedRows,
    defaultValue: props.defaultSelectedRows,
    onChange: props.onRowSelectionChange,
  });

  options.state = {
    ...options.state,
    rowSelection: selectedRows ?? {},
  };

  options.onRowSelectionChange = setSelectedRows;
}
